import { Role } from '@sendbird/chat';
import type { Member } from '@sendbird/chat/groupChannel';
import { useChannelSettingsContext } from '@sendbird/uikit-react/ChannelSettings/context';
import { useLocalization } from '@sendbird/uikit-react/hooks/useLocalization';
import {
  UserListItem,
  type UserListItemProps,
} from '@sendbird/uikit-react/ui/UserListItem';
import UserListItemMenu from '@sendbird/uikit-react/ui/UserListItemMenu';
import {
  Fragment,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useTranslate } from 'ra-core';
import { useMuteUser } from '../../../api/hooks/sendbird/useMuteUser';
import { InviteUsersModal } from './InviteUsersModal';
import { AllMembersModal } from './AllMembersModal';
import { useKickMember } from '../../../api/hooks/sendbird/useKickMember';
import { Button } from '../../../components/ui/button';
import { ChannelSettingsMenuItem } from './ChannelSettingsMenuItem';

interface MemberListProps {
  canInviteUsers: boolean;
  renderUserListItem?: (
    props: UserListItemProps & { index: number }
  ) => ReactNode;
}
export const MemberList = ({
  canInviteUsers,
  renderUserListItem: RenderUserListItem = UserListItem,
}: MemberListProps): ReactElement => {
  /**  Remember `members` can be incomplete list of members, since the initial list is limited to 10
   * To get the full list of members we fetch the full list when the user clicks "Show all" or "Invite members" */
  const [members, setMembers] = useState<Array<Member>>([]);
  const [hasNext, setHasNext] = useState(false);
  const [showAllMembers, setShowAllMembers] = useState(false);
  const [showInviteUsers, setShowInviteUsers] = useState(false);
  const { channel, forceUpdateUI } = useChannelSettingsContext();
  const { stringSet } = useLocalization();
  const translate = useTranslate();

  const handleUpdateMembers = (id: string) =>
    setMembers(members.filter((member) => member.userId !== id));

  const { mutate: kickMember, isPending: isKickPending } = useKickMember({
    onSuccess: (_data, variables) => handleUpdateMembers(variables.userId),
  });
  const { mutate: patchMuteUser, isPending: isMutePending } = useMuteUser();

  const handleUpdateMuteState = (id: string, isMuted: boolean) =>
    setMembers((prevMembers) => {
      const newMembers = [...prevMembers];
      for (const newMember of newMembers) {
        if (newMember.userId === id) {
          newMember.isMuted = isMuted;
          break;
        }
      }

      return newMembers;
    });

  const refreshList = useCallback(() => {
    const memberUserListQuery = channel.createMemberListQuery({
      // Short list of users. If the user wants to see more, they can click the "Show all" button, which presents a modal
      limit: 10,
    });
    void memberUserListQuery.next().then((members) => {
      setMembers(members);
      setHasNext(memberUserListQuery.hasNext);
    });
  }, [channel]);

  useEffect(refreshList, [channel.url, refreshList]);

  return (
    <div className="sendbird-channel-settings-member-list">
      {members.map((member, index) => (
        <Fragment key={member.userId}>
          <RenderUserListItem
            index={index}
            user={member}
            channel={channel}
            size="small"
            avatarSize="24px"
            renderListItemMenu={
              channel.myRole === Role.OPERATOR
                ? (props) => (
                    <UserListItemMenu
                      {...props}
                      onToggleOperatorState={({ newStatus: isOperator }) => {
                        const newMembers = [...members];
                        for (const newMember of newMembers) {
                          if (newMember.userId === member.userId) {
                            newMember.role = isOperator
                              ? Role.OPERATOR
                              : Role.NONE;
                            break;
                          }
                        }
                        setMembers(newMembers);
                      }}
                      renderMenuItems={({ items }) => {
                        const { OperatorToggleMenuItem } = items;

                        return (
                          <>
                            <OperatorToggleMenuItem />
                            <ChannelSettingsMenuItem
                              key={`mute-${member.userId}`}
                              pending={isMutePending}
                              onClick={() => {
                                patchMuteUser(
                                  {
                                    channelUrl: channel.url,
                                    userId: member.userId,
                                    muted: !member.isMuted,
                                  },
                                  {
                                    onSuccess: () => {
                                      handleUpdateMuteState(
                                        member.userId,
                                        !member.isMuted
                                      );
                                    },
                                  }
                                );
                              }}
                            >
                              {member.isMuted
                                ? translate('chat.unmute')
                                : translate('chat.mute')}
                            </ChannelSettingsMenuItem>

                            <ChannelSettingsMenuItem
                              key={`kick-${member.userId}`}
                              onClick={() =>
                                kickMember({
                                  userId: member.userId,
                                  channelUrl: channel.url,
                                })
                              }
                              pending={isKickPending}
                              destructive
                            >
                              {translate('chat.kick')}
                            </ChannelSettingsMenuItem>
                          </>
                        );
                      }}
                    />
                  )
                : undefined
            }
          />
        </Fragment>
      ))}
      <div className="sendbird-channel-settings-accordion__footer">
        {hasNext && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowAllMembers(true)}
          >
            {stringSet.CHANNEL_SETTING__MEMBERS__SEE_ALL_MEMBERS}
          </Button>
        )}
        {canInviteUsers && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowInviteUsers(true)}
          >
            {stringSet.CHANNEL_SETTING__MEMBERS__INVITE_MEMBER}
          </Button>
        )}
      </div>
      <AllMembersModal
        open={showAllMembers}
        onCancel={() => {
          setShowAllMembers(false);
          refreshList();
          forceUpdateUI();
        }}
      />

      <InviteUsersModal
        open={showInviteUsers}
        onCancel={() => setShowInviteUsers(false)}
        onSubmit={() => {
          setShowInviteUsers(false);
          refreshList();
          forceUpdateUI();
        }}
      />
    </div>
  );
};
